import React from 'react';

export const DEFAULT_LINE_ITEM = { description: '', quantity: 1, unitPriceCents: 0 };

export const CURRENCY_OPTIONS = [
    { value: 'USD', label: 'USD' },
    { value: 'EUR', label: 'EUR' },
    { value: 'GBP', label: 'GBP' },
    { value: 'CAD', label: 'CAD' },
    { value: 'AUD', label: 'AUD' },
];

export function centsToDollars(cents = 0) {
    return ((Number(cents) || 0) / 100).toFixed(2);
}

export function centsToEditableDollars(cents = 0) {
    return String((Number(cents) || 0) / 100);
}

export function dollarsToCents(value) {
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.round(parsed * 100));
}

export function formatCents(cents = 0, currency = 'USD') {
    return `$${centsToDollars(cents)} ${currency}`;
}

export function normalizeLineItems(lineItems = []) {
    return lineItems.map(item => ({
        description: (item.description || '').trim(),
        quantity: Math.max(1, Number.parseInt(item.quantity, 10) || 1),
        unitPriceCents: Math.max(0, Number.parseInt(item.unitPriceCents, 10) || 0),
    }));
}

export function InvoiceFormSection({ title, description, children, className = '' }) {
    return (
        <div className={`invoice-form-section ${className}`}>
            <div className="invoice-form-section__header">
                <h3>{title}</h3>
                {description && <p>{description}</p>}
            </div>
            <div className="invoice-form-section__body">
                {children}
            </div>
        </div>
    );
}

export function InvoiceLineItemsEditor({ lineItems = [], onChange, currency = 'USD' }) {
    const updateItem = (index, field, value) => {
        const next = [...lineItems];
        next[index] = { ...next[index], [field]: value };
        onChange(next);
    };

    const addItem = () => {
        onChange([...lineItems, { ...DEFAULT_LINE_ITEM }]);
    };

    const removeItem = (index) => {
        onChange(lineItems.filter((_, itemIndex) => itemIndex !== index));
    };

    return (
        <div className="invoice-line-items-editor">
            <div className="invoice-line-item-grid invoice-line-item-grid--header" aria-hidden="true">
                <span>Description</span>
                <span>Qty</span>
                <span>Unit price</span>
                <span>Total</span>
                <span></span>
            </div>

            {lineItems.map((item, index) => {
                const quantity = Math.max(1, Number.parseInt(item.quantity, 10) || 1);
                const totalCents = quantity * (Number.parseInt(item.unitPriceCents, 10) || 0);
                const canRemove = lineItems.length > 1;

                return (
                    <div className="invoice-line-item-grid invoice-line-item-row" key={index}>
                        <label className="invoice-line-item-field invoice-line-item-field--description">
                            <span>Description</span>
                            <input
                                type="text"
                                placeholder="Service, product, or custom work"
                                value={item.description}
                                onChange={e => updateItem(index, 'description', e.target.value)}
                                required
                            />
                        </label>

                        <label className="invoice-line-item-field invoice-line-item-field--quantity">
                            <span>Qty</span>
                            <input
                                type="number"
                                min={1}
                                value={item.quantity}
                                onChange={e => updateItem(index, 'quantity', Math.max(1, Number.parseInt(e.target.value, 10) || 1))}
                            />
                        </label>

                        <label className="invoice-line-item-field invoice-line-item-field--price">
                            <span>Unit price</span>
                            <input
                                type="number"
                                min={0}
                                step="0.01"
                                value={centsToEditableDollars(item.unitPriceCents)}
                                onChange={e => updateItem(index, 'unitPriceCents', dollarsToCents(e.target.value))}
                            />
                        </label>

                        <div className="invoice-line-item-total">
                            <span>Total</span>
                            <strong>{formatCents(totalCents, currency)}</strong>
                        </div>

                        <button
                            type="button"
                            className="button small danger invoice-line-item-remove"
                            onClick={() => removeItem(index)}
                            disabled={!canRemove}
                            title={canRemove ? 'Remove line item' : 'At least one line item is required'}
                        >
                            <i className="fas fa-trash-alt" aria-hidden="true"></i>
                            <span className="sr-only">Remove line item</span>
                        </button>
                    </div>
                );
            })}

            <button type="button" className="button secondary small invoice-add-line-item" onClick={addItem}>
                <i className="fas fa-plus" aria-hidden="true"></i>
                Add line item
            </button>
        </div>
    );
}

export function InvoiceTotalsSummary({
    subtotalCents,
    taxCents = 0,
    shippingCents = 0,
    discountCents = 0,
    totalCents,
    currency = 'USD',
}) {
    return (
        <div className="invoice-summary" role="region" aria-label="Invoice totals">
            <div>
                <span>Subtotal</span>
                <strong>{formatCents(subtotalCents, currency)}</strong>
            </div>
            <div>
                <span>Tax</span>
                <span>{formatCents(taxCents, currency)}</span>
            </div>
            <div>
                <span>Shipping</span>
                <span>{formatCents(shippingCents, currency)}</span>
            </div>
            <div>
                <span>Discount</span>
                <span>-{formatCents(discountCents, currency)}</span>
            </div>
            <div className="invoice-summary__total">
                <span>Total</span>
                <strong>{formatCents(totalCents, currency)}</strong>
            </div>
        </div>
    );
}
